import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Collapse,
  Switch,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import keyboardArrowDown from "@iconify/icons-eva/arrow-ios-downward-fill";
import keyboardArrowUp from "@iconify/icons-eva/arrow-ios-upward-fill";
import { formatearFecha } from "src/utils/formatTime";
import SucursalFilter from "src/components/estadisticas/sucursal-filter";
import { useSelector } from "react-redux";
import Scrollbar from "src/components/Scrollbar";
import { formatAsCLP } from "src/utils/formatNumber";
import { clientesApi } from "src/services/clientesApi";
import { planesApi } from "src/services/planesApi";
import dayjs from "dayjs";
import { formatRut, RutFormat } from "@fdograph/rut-utilities";
import { useSnackbar } from "src/components/snackbar";

// Componente para cada fila de cliente (con expansión)
function ClienteMorosoRow({ cliente, onToggleFacturacion, isUpdating }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            <Icon icon={open ? keyboardArrowUp : keyboardArrowDown} />
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="medium">
            {cliente.nombre}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {formatRut(cliente.rut, RutFormat.DOTS_DASH)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {(() => {
            const facturaMasAntigua = cliente.facturasImpagas[0];
            const mesesAtraso = dayjs().diff(
              dayjs(facturaMasAntigua.fecha, "YYYY/MM/DD"),
              "months",
            );

            if (mesesAtraso >= 6)
              return <Chip label="Urgente" color="error" size="small" />;
            if (mesesAtraso >= 4)
              return <Chip label="Alta" color="warning" size="small" />;
            return <Chip label="Normal" color="default" size="small" />;
          })()}
        </TableCell>
        <TableCell align="center">
          <Chip
            label={cliente.totalFacturasImpagas}
            color="error"
            size="small"
          />
        </TableCell>
        <TableCell align="center">{cliente.planes.length}</TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight="medium" color="error">
            {formatAsCLP(cliente.totalDeuda || 0)}
          </Typography>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              {/* SECCIÓN DE PLANES */}
              <Typography variant="h6" gutterBottom component="div">
                Planes del Cliente
              </Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Plan</TableCell>
                    <TableCell>Sucursal</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="center">Facturación Activa</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cliente.planes.map((plan, index) => (
                    <TableRow key={index}>
                      <TableCell>{plan.nombreTipoPlan}</TableCell>
                      <TableCell>{plan.sucursal}</TableCell>
                      <TableCell align="right">
                        {plan.tipoMoneda === "PESOS" ? "" : plan.tipoMoneda}{" "}
                        {formatAsCLP(plan.valor)}
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={plan.seFacturaGestor === 1}
                          onChange={() => onToggleFacturacion(plan)}
                          color="primary"
                          disabled={isUpdating}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* SECCIÓN DE FACTURAS IMPAGAS */}
              <Typography variant="h6" gutterBottom component="div">
                Facturas Impagas ({cliente.facturasImpagas.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Folio</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Fecha Emisión</TableCell>
                    <TableCell>Fecha Vencimiento</TableCell>
                    <TableCell>Días Vencido</TableCell>
                    <TableCell>Sucursal</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cliente.facturasImpagas.map((factura) => (
                    <TableRow key={`${factura.tipo}-${factura.id}`}>
                      <TableCell>{factura.folio}</TableCell>
                      <TableCell>
                        <Chip
                          label={factura.tipo}
                          size="small"
                          color={
                            factura.tipo === "PLAN" ? "primary" : "secondary"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {formatearFecha(factura.fecha, "DD/MM/YYYY")}
                      </TableCell>
                      <TableCell>
                        {formatearFecha(
                          dayjs(factura.fecha, "YYYY/MM/DD")
                            .add(29, "days")
                            .format("YYYY/MM/DD"),
                          "DD/MM/YYYY",
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const fechaVencimiento = dayjs(
                            factura.fecha,
                            "YYYY/MM/DD",
                          ).add(29, "days");
                          const diasVencido = dayjs().diff(
                            fechaVencimiento,
                            "days",
                          );
                          return diasVencido > 0 ? (
                            <Chip
                              label={`${diasVencido} días`}
                              size="small"
                              color="error"
                            />
                          ) : (
                            <Chip
                              label="Por vencer"
                              size="small"
                              color="warning"
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>{factura.sucursal}</TableCell>
                      <Tooltip
                        title={
                          factura.estado === "ABONO"
                            ? `Valor original: ${formatAsCLP(
                                factura.valorOriginal,
                              )} | Saldo pendiente: ${formatAsCLP(
                                factura.valor,
                              )}`
                            : ""
                        }
                      >
                        <TableCell align="right">
                          {factura.estado === "ABONO" ? (
                            <Box>
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ textDecoration: "line-through" }}
                              >
                                {formatAsCLP(factura.valorOriginal)}
                              </Typography>
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                color="error"
                              >
                                {formatAsCLP(factura.valor)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2">
                              {formatAsCLP(factura.valor)}
                            </Typography>
                          )}
                        </TableCell>
                      </Tooltip>
                      <TableCell>
                        <Chip
                          label={factura.estado}
                          size="small"
                          color={
                            factura.estado === "ABONO" ? "warning" : "default"
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={6} align="right">
                      <strong>Total Adeudado:</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>
                        {formatAsCLP(
                          cliente.facturasImpagas.reduce(
                            (sum, f) => sum + f.valor,
                            0,
                          ),
                        )}
                      </strong>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function ClientesMorososDialog({ open, onClose }) {
  const { empleado } = useSelector((state) => state.auth.user);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sucursalesSeleccionadas, setSucursalesSeleccionadas] = useState([]);

  const { enqueueSnackbar } = useSnackbar();

  // RTK Query
  const [obtenerMorosos, { data, isLoading, isFetching }] =
    clientesApi.useLazyObtenerClientesMorososQuery();
  const [editarFacturacion, { isLoading: isUpdating }] =
    planesApi.useEditarFacturacionGestorMutation();

  const fechaLimite = data?.fechaLimite || "";
  const clientesMorosos = useMemo(
    () => data?.clientesMorosos || [],
    [data?.clientesMorosos],
  );

  useEffect(() => {
    if (open) {
      obtenerMorosos({ sucursales: sucursalesSeleccionadas });
    }
  }, [open, sucursalesSeleccionadas, obtenerMorosos]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSetSucursalesSeleccionadas = useCallback((sucursales) => {
    setSucursalesSeleccionadas(sucursales);
  }, []);

  const handleToggleFacturacion = async (plan) => {
    try {
      const nuevoEstado = plan.seFacturaGestor === 1 ? 0 : 1;

      await editarFacturacion({
        refPlan: plan.idPlan,
        refTipoPlan: plan.nombreTipoPlan,
        seFacturaGestor: nuevoEstado,
      }).unwrap();

      enqueueSnackbar(
        `Facturación ${
          nuevoEstado === 1 ? "activada" : "desactivada"
        } correctamente`,
        { variant: "success" },
      );

      // Refrescar datos
      obtenerMorosos({ sucursales: sucursalesSeleccionadas });
    } catch (error) {
      enqueueSnackbar("Error al actualizar la facturación", {
        variant: "error",
      });
    }
  };

  const clientesPaginados = useMemo(() => {
    return clientesMorosos.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [clientesMorosos, page, rowsPerPage]);

  const totalClientes = clientesMorosos.length;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h5">Clientes Morosos</Typography>
              {totalClientes > 0 && (
                <Typography variant="caption" color="textSecondary">
                  Total: {totalClientes} cliente{totalClientes !== 1 ? "s" : ""}
                </Typography>
              )}
            </Box>
            {fechaLimite && (
              <Typography variant="caption" color="textSecondary">
                Facturas con antigüedad desde:{" "}
                {formatearFecha(fechaLimite, "DD/MM/YYYY")}
              </Typography>
            )}
          </Stack>
        </DialogTitle>

        <DialogContent>
          {/* FILTRO DE SUCURSALES */}
          <Box sx={{ mb: 3 }}>
            <SucursalFilter
              id="filtro-sucursales"
              label="Filtrar por sucursales"
              rutEmpleado={empleado.rut}
              sucursalesSeleccionadas={sucursalesSeleccionadas}
              onChangeSucursales={handleSetSucursalesSeleccionadas}
            />
          </Box>

          {/* INFORMACIÓN */}
          <Alert severity="info" sx={{ mb: 2 }}>
            Se muestran clientes con <strong>2 o más facturas impagas</strong>{" "}
            con antigüedad mayor a 2 meses. Puedes desactivar la facturación
            automática de planes usando el switch.
          </Alert>

          {/* TABLA */}
          {isLoading || isFetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : totalClientes === 0 ? (
            <Alert severity="success">
              ¡Excelente! No hay clientes morosos en este momento.
            </Alert>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="50px" />
                      <TableCell>Cliente</TableCell>
                      <TableCell align="center">Prioridad</TableCell>
                      <TableCell align="center">Facturas Impagas</TableCell>
                      <TableCell align="center">Planes Activos</TableCell>
                      <TableCell align="right">Total Deuda</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientesPaginados.map((cliente) => (
                      <ClienteMorosoRow
                        key={cliente.rut}
                        cliente={cliente}
                        onToggleFacturacion={handleToggleFacturacion}
                        isUpdating={isUpdating}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalClientes}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
              />
            </Scrollbar>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
